import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Check, X, ClipboardList } from "lucide-react";
import { Card } from "../components/UI";
import { PageLoader } from "../components/LoadingSpinner";
import { ErrorAlert, InlineError } from "../components/ErrorAlert";
import questionnaireApi from "../api/questionnaire";

const INACTIVE_TAB = "bg-gray-100 text-gray-600 hover:bg-gray-200";

export default function EPDSManagement() {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [activeQuestionnaire, setActiveQuestionnaire] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mutateError, setMutateError] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editCat, setEditCat] = useState("");
  const [newQuestion, setNewQuestion] = useState("");
  const [saving, setSaving] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [answerOptions, setAnswerOptions] = useState([
    { label: "", emoji: "", value: 0 },
  ]);

  // ── Fetch questionnaires ──────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await questionnaireApi.getAll();
      const data = res.data?.data ?? res.data ?? [];
      setQuestionnaires(data);
      if (data.length > 0) {
        const detail = await questionnaireApi.getDetail(data[0].id);
        setActiveQuestionnaire(detail.data.data);
      }
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Gagal memuat kuesioner",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const active = activeQuestionnaire;
  const questions = activeQuestionnaire?.questions ?? [];

  const updateActiveQuestions = (fn) => {
    setActiveQuestionnaire((prev) =>
      prev ? { ...prev, questions: fn(prev.questions || []) } : prev,
    );
  };

  // ── Tab change ────────────────────────────────────────────────────────────
  const handleTabChange = async (idx, questionnaireId) => {
    if (idx === activeIdx || tabLoading) return;
    setTabLoading(true);
    setMutateError("");
    closeModal();
    try {
      const detail = await questionnaireApi.getDetail(questionnaireId);
      setActiveQuestionnaire(detail.data.data);
      setActiveIdx(idx);
    } catch (err) {
      setMutateError("Gagal memuat soal kuesioner");
    } finally {
      setTabLoading(false);
    }
  };

  // ── Modal ─────────────────────────────────────────────────────────────────
  const openAdd = () => {
    setNewQuestion("");
    setEditCat("");
    setAnswerOptions([{ label: "", emoji: "", value: 0 }]);
    setMutateError("");
    setModalMode("add");
  };

  const startEdit = (q) => {
    setEditingId(q.id);
    setEditText(q.question);
    setEditCat(String(q.questionnaire_id));
    setAnswerOptions(
      q.answer_options?.length > 0
        ? q.answer_options.map((o) => ({
            id: o.id,
            label: o.label,
            emoji: o.emoji ?? "",
            value: o.value,
          }))
        : [{ label: "", emoji: "", value: 0 }],
    );
    setMutateError("");
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingId(null);
    setMutateError("");
  };

  const refreshQuestionnaire = async (id) => {
    const detail = await questionnaireApi.getDetail(id);
    const data = detail.data.data;
    setActiveQuestionnaire(data);
    setQuestionnaires((prev) =>
      prev.map((q) =>
        q.id === id
          ? {
              ...q,
              questions_count: data.questions?.length ?? q.questions_count,
            }
          : q,
      ),
    );
    return data;
  };

  const refreshAll = async (activeQuestionnaireId) => {
    const listRes = await questionnaireApi.getAll();
    const list = listRes.data?.data ?? listRes.data ?? [];
    setQuestionnaires(list);

    const detail = await questionnaireApi.getDetail(activeQuestionnaireId);
    setActiveQuestionnaire(detail.data.data);
  };

  // ── Add ───────────────────────────────────────────────────────────────────
  const addQuestion = async () => {
    if (!newQuestion.trim() || !editCat) {
      setMutateError("Isi pertanyaan dan pilih questionnaire");
      return;
    }
    setSaving(true);
    setMutateError("");
    try {
      const res = await questionnaireApi.addQuestion(active.id, {
        question: newQuestion.trim(),
        questionnaire_id: editCat,
        answer_options: answerOptions.filter((o) => o.label.trim()), // ← tambah ini
      });
      const newQ = res.data?.data ?? res.data;
      updateActiveQuestions((prev) => [
        ...prev,
        { ...newQ, no: prev.length + 1 },
      ]);
      closeModal();
    } catch (err) {
      setMutateError(err.response?.data?.message || "Gagal menambah soal");
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const saveEdit = async () => {
    if (!editText.trim() || !editCat) {
      setMutateError("Isi pertanyaan dan pilih questionnaire");
      return;
    }
    setSaving(true);
    setMutateError("");
    try {
      await questionnaireApi.updateQuestion(editingId, {
        question: editText.trim(),
        questionnaire_id: editCat,
        order: active?.questions?.findIndex((q) => q.id === editingId) + 1 ?? 0,
        answer_options: answerOptions.filter((o) => o.label.trim()),
      });
      updateActiveQuestions((prev) =>
        prev.map((q) =>
          q.id === editingId
            ? {
                ...q,
                question: editText.trim(),
                answer_options: answerOptions.filter((o) => o.label.trim()),
              }
            : q,
        ),
      );
      closeModal();
    } catch (err) {
      setMutateError(err.response?.data?.message || "Gagal menyimpan soal");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const deleteQuestion = async (id) => {
    if (!window.confirm("Hapus soal ini?")) return;
    setMutateError("");
    try {
      await questionnaireApi.deleteQuestion(id);
      await refreshQuestionnaire(active.id);
      await refreshAll(active.id);
    } catch (err) {
      setMutateError(err.response?.data?.message || "Gagal menghapus soal");
    }
  };

  if (loading)
    return (
      <div className="p-6">
        <PageLoader message="Memuat kuesioner..." />
      </div>
    );
  if (error)
    return (
      <div className="p-6">
        <ErrorAlert message={error} onRetry={fetchAll} />
      </div>
    );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="font-display font-800 text-2xl text-gray-900">
            Kelola Kuesioner
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Kelola soal kuesioner per kategori
          </p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-800 hover:bg-primary-900 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" /> Tambah Soal
        </button>
      </div>

      {/* Category tabs */}
      {questionnaires.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {questionnaires.map((q, i) => (
            <button
              key={q.id}
              onClick={() => handleTabChange(i, q.id)}
              disabled={tabLoading}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border
                ${
                  activeIdx === i
                    ? "bg-primary-800 text-white border-transparent shadow-sm"
                    : `${INACTIVE_TAB} border-gray-200`
                }
                ${tabLoading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {q.title}
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full ${activeIdx === i ? "bg-white/20" : "bg-gray-200 text-gray-500"}`}
              >
                {q.questions_count ?? 0}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Active questionnaire info */}
      {active && (
        <div className="bg-primary-50 border border-primary-100 rounded-2xl p-4 mb-5 flex items-start gap-3">
          <ClipboardList className="w-5 h-5 text-primary-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-primary-800 mb-0.5">
              {active.title}
            </p>
            <p className="text-xs text-primary-700">
              {active.description || ""} — {questions.length} soal aktif
            </p>
          </div>
        </div>
      )}

      <InlineError message={mutateError} />

      {/* Questions list */}
      <Card className="overflow-hidden mt-3">
        {tabLoading ? (
          <div className="divide-y divide-gray-50 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-4 flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-gray-200 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : questions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-gray-400">
            <ClipboardList className="w-10 h-10 mb-3 opacity-25" />
            <p className="text-sm">
              Belum ada soal. Klik "Tambah Soal" untuk mulai.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {questions.map((q) => (
              <div
                key={q.id}
                className="p-4 hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-800 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {q.no}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 leading-relaxed mb-1.5">
                      {q.question}
                    </p>
                    {q.category && (
                      <span className="text-xs text-accent-700 bg-accent-50 px-2.5 py-0.5 rounded-full border border-accent-100">
                        {q.category}
                      </span>
                    )}
                    {q.answer_options?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {q.answer_options.map((opt, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 text-xs bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full text-gray-600"
                          >
                            {opt.emoji && <span>{opt.emoji}</span>}
                            {opt.label}
                            <span className="text-gray-400 font-semibold">
                              ({opt.value})
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => startEdit(q)}
                      className="p-2 text-gray-400 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteQuestion(q.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Modal Tambah / Edit */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeModal}
          />

          {/* Modal card */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="font-display font-700 text-gray-900">
                  {modalMode === "add" ? "Tambah Soal Baru" : "Edit Soal"}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">{active?.title}</p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              <InlineError message={mutateError} />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Pertanyaan
                </label>
                <textarea
                  value={modalMode === "add" ? newQuestion : editText}
                  onChange={(e) =>
                    modalMode === "add"
                      ? setNewQuestion(e.target.value)
                      : setEditText(e.target.value)
                  }
                  placeholder="Tuliskan pertanyaan di sini..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Questionnaire
                </label>
                <select
                  value={editCat}
                  onChange={(e) => setEditCat(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  <option value="" disabled selected>
                    Pilih Questionnaire
                  </option>
                  {questionnaires.map((q) => (
                    <option key={q.id} value={String(q.id)}>
                      {q.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Answer Options */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Pilihan Jawaban
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setAnswerOptions((prev) => [
                        ...prev,
                        { label: "", emoji: "", value: prev.length },
                      ])
                    }
                    className="flex items-center gap-1 text-xs text-primary-700 hover:text-primary-900 font-semibold"
                  >
                    <Plus className="w-3.5 h-3.5" /> Tambah Opsi
                  </button>
                </div>

                <div className="space-y-2">
                  {answerOptions.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2">
                      {/* Emoji */}
                      <input
                        type="text"
                        value={opt.emoji}
                        onChange={(e) =>
                          setAnswerOptions((prev) =>
                            prev.map((o, idx) =>
                              idx === i ? { ...o, emoji: e.target.value } : o,
                            ),
                          )
                        }
                        placeholder="😊"
                        className="w-12 px-2 py-2 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {/* Label */}
                      <input
                        type="text"
                        value={opt.label}
                        onChange={(e) =>
                          setAnswerOptions((prev) =>
                            prev.map((o, idx) =>
                              idx === i ? { ...o, label: e.target.value } : o,
                            ),
                          )
                        }
                        placeholder="Contoh: Tidak pernah"
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {/* Value */}
                      <input
                        type="number"
                        min={0}
                        max={5}
                        value={opt.value}
                        onChange={(e) =>
                          setAnswerOptions((prev) =>
                            prev.map((o, idx) =>
                              idx === i ? { ...o, value: +e.target.value } : o,
                            ),
                          )
                        }
                        className="w-16 px-2 py-2 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {/* Hapus */}
                      {answerOptions.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setAnswerOptions((prev) =>
                              prev.filter((_, idx) => idx !== i),
                            )
                          }
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-400 mt-2">
                  Emoji opsional • Label wajib • Value = skor (0–5)
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-2 p-5 border-t border-gray-100">
              <button
                onClick={modalMode === "add" ? addQuestion : saveEdit}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary-800 text-white text-sm font-semibold rounded-xl hover:bg-primary-900 disabled:opacity-60 transition-colors"
              >
                <Check className="w-4 h-4" />
                {saving
                  ? "Menyimpan..."
                  : modalMode === "add"
                    ? "Simpan Soal"
                    : "Simpan Perubahan"}
              </button>
              <button
                onClick={closeModal}
                disabled={saving}
                className="px-5 py-2.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-200 disabled:opacity-60 transition-colors"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
